import { useEffect, useState } from 'react';
import { Briefcase } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useWorkspace } from '@/hooks/useWorkspace';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { showToast } from '@/components/ui/toaster';
import { BottomSheet } from '@/components/common/BottomSheet';
import DatePicker from '@/components/common/DatePicker';
import { hrRequestService, type HrRequestType, type ReimbursementOcrResult } from '@/services/hr-request.service';
import { teamService } from '@/services/team.service';
import { uploadService } from '@/services/upload.service';

const TYPE_OPTIONS: { value: HrRequestType; labelKey: string }[] = [
    { value: 'overtime', labelKey: 'ot.type.overtime' },
    { value: 'lateness_excuse', labelKey: 'ot.type.lateness_excuse' },
    { value: 'reimbursement', labelKey: 'ot.type.reimbursement' },
    { value: 'visit', labelKey: 'ot.type.visit' },
    { value: 'sick_leave', labelKey: 'ot.type.sick_leave' },
    { value: 'permission_leave', labelKey: 'ot.type.permission_leave' },
    { value: 'annual_leave', labelKey: 'ot.type.annual_leave' },
    { value: 'maternity_leave', labelKey: 'ot.type.maternity_leave' },
];

const REIMBURSEMENT_CATS = ['transport', 'makan', 'visit', 'lainnya'];

const LEAVE_TYPES = new Set<HrRequestType>(['sick_leave', 'permission_leave', 'annual_leave', 'maternity_leave']);

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCreated: () => void;
}

export default function OvertimeFormSheet({ open, onOpenChange, onCreated }: Props) {
    const { t } = useI18n();
    const { currentWorkspace } = useWorkspace();
    const [teams, setTeams] = useState<any[]>([]);
    const [type, setType] = useState<HrRequestType>('overtime');
    const [teamId, setTeamId] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [fromTime, setFromTime] = useState('09:00');
    const [toDate, setToDate] = useState('');
    const [toTime, setToTime] = useState('17:00');
    const [reason, setReason] = useState('');
    const [hours, setHours] = useState('');
    const [amount, setAmount] = useState('');
    const [category, setCategory] = useState('transport');
    const [location, setLocation] = useState('');
    const [attachments, setAttachments] = useState<{ url: string; filename: string }[]>([]);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [ocrBusy, setOcrBusy] = useState(false);
    const [ocrSummary, setOcrSummary] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open || !currentWorkspace) return;
        teamService.listByWorkspace(currentWorkspace._id).then((res: any) => {
            const list = (res.data as any[]) || [];
            setTeams(list);
            if (list.length > 0 && !teamId) setTeamId(list[0]._id || list[0].id);
        }).catch(() => {});
    }, [open, currentWorkspace?._id]);

    const reset = () => {
        setType('overtime'); setTeamId(''); setFromDate(''); setFromTime('09:00');
        setToDate(''); setToTime('17:00'); setReason(''); setHours('');
        setAmount(''); setCategory('transport'); setLocation(''); setAttachments([]);
        setOcrSummary(null);
    };

    const composeDateTime = (date: string, time: string): string | null => {
        if (!date) return null;
        const [h = '00', m = '00'] = (time || '00:00').split(':');
        const d = new Date(date + 'T00:00:00');
        d.setHours(parseInt(h, 10), parseInt(m, 10), 0, 0);
        return d.toISOString();
    };

    const applyOcrDraft = (draft: ReimbursementOcrResult) => {
        if (draft.amount > 0) setAmount(String(draft.amount));
        if (REIMBURSEMENT_CATS.includes(draft.category)) setCategory(draft.category);
        if (draft.transactionDate) setFromDate(draft.transactionDate);
        if (draft.reason && !reason.trim()) setReason(draft.reason);

        const nominal = new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: draft.currency || 'IDR',
            maximumFractionDigits: 0,
        }).format(draft.amount || 0);
        const confidence = Math.round((draft.confidence || 0) * 100);
        setOcrSummary(`${draft.merchantName || 'Struk'} - ${nominal} - confidence ${confidence}%`);
    };

    const runReimbursementOcr = async (file: File) => {
        if (!currentWorkspace?._id || type !== 'reimbursement' || !file.type.startsWith('image/')) return;

        setOcrBusy(true);
        setOcrSummary(null);
        try {
            const res = await hrRequestService.ocrReimbursement(currentWorkspace._id, file);
            const draft = res.data as ReimbursementOcrResult;
            applyOcrDraft(draft);
            showToast({ variant: 'success', description: 'AI OCR reimbursement berhasil mengisi draft' });
        } catch (err: any) {
            showToast({
                variant: 'destructive',
                description: err?.response?.data?.error || 'AI OCR reimbursement gagal',
            });
        } finally {
            setOcrBusy(false);
        }
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingAttachment(true);
        try {
            const folder = type === 'visit' ? 'visit-photos' : 'hr-attachments';
            const res = await uploadService.uploadToFolder(file, folder);
            const data = (res.data as any)?.data ?? res.data;
            setAttachments(prev => [...prev, { url: data.url, filename: data.filename || file.name }]);
            await runReimbursementOcr(file);
        } catch (err: any) {
            showToast({ variant: 'destructive', description: err?.response?.data?.error || 'Gagal upload' });
        } finally {
            setUploadingAttachment(false);
            e.target.value = '';
        }
    };

    const removeAttachment = (idx: number) => {
        setAttachments(attachments.filter((_, i) => i !== idx));
    };

    const submit = async () => {
        if (!currentWorkspace) return;
        if (!teamId) {
            showToast({ variant: 'destructive', description: t('ot.form.team_required') });
            return;
        }
        const fromIso = composeDateTime(fromDate, fromTime);
        if (!fromIso) {
            showToast({ variant: 'destructive', description: t('ot.form.from_required') });
            return;
        }
        const toIso = toDate ? composeDateTime(toDate, toTime) : null;
        setBusy(true);
        try {
            const payload: Record<string, any> = { reason };
            if (type === 'overtime') payload.hours = Number(hours) || 0;
            if (type === 'reimbursement') payload.category = category;
            if (type === 'visit') payload.location = location;
            if (attachments.length > 0) payload.attachments = attachments;

            await hrRequestService.create({
                workspaceId: currentWorkspace._id,
                teamId,
                type,
                fromAt: fromIso,
                toAt: toIso || undefined,
                payload,
                amount: type === 'reimbursement' ? Number(amount) || 0 : 0,
            });
            showToast({ variant: 'success', description: t('ot.form.toast_submitted') });
            reset();
            onCreated();
        } catch (e: any) {
            showToast({ variant: 'destructive', description: e?.response?.data?.error || t('common.save_failed') });
        } finally {
            setBusy(false);
        }
    };

    const isLeave = LEAVE_TYPES.has(type);
    const allowAttachment = type === 'reimbursement' || type === 'visit' || type === 'sick_leave';

    return (
        <BottomSheet
            open={open}
            onOpenChange={onOpenChange}
            title={t('ot.form.title')}
            footer={
                <div className="flex gap-2">
                    <Button onClick={submit} disabled={busy} className="flex-1">{busy ? t('common.saving') : t('ot.form.submit')}</Button>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
                </div>
            }
        >
            <div className="space-y-3">
                <Field label={t('ot.form.type')}>
                    <Select value={type} onValueChange={(v) => setType(v as HrRequestType)}>
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{t(o.labelKey)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Field>

                <Field label={t('ot.form.team')}>
                    <Select value={teamId} onValueChange={setTeamId}>
                        <SelectTrigger className="text-sm"><SelectValue placeholder={t('ot.form.team_placeholder')} /></SelectTrigger>
                        <SelectContent>
                            {teams.map((tm) => <SelectItem key={tm._id || tm.id} value={tm._id || tm.id}>{tm.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </Field>

                <div>
                    <label className="text-xs font-bold text-gray-700 mb-1 block">{isLeave ? t('ot.form.leave_from') : t('ot.form.from')}</label>
                    <div className={isLeave ? '' : 'grid grid-cols-[1fr_90px] gap-2'}>
                        <DatePicker value={fromDate} onChange={setFromDate} presets placeholder={t('ot.form.from_date_placeholder')} />
                        {!isLeave && <input type="time" value={fromTime} onChange={(e) => setFromTime(e.target.value)} className="px-3 py-2 text-sm rounded-xl bg-white" style={{ border: '1px solid #d0c8be' }} />}
                    </div>
                </div>
                <div>
                    <label className="text-xs font-bold text-gray-700 mb-1 block">{isLeave ? t('ot.form.leave_to') : t('ot.form.to')}</label>
                    <div className={isLeave ? '' : 'grid grid-cols-[1fr_90px] gap-2'}>
                        <DatePicker value={toDate} onChange={setToDate} placeholder={t('ot.form.to_date_placeholder')} minDate={fromDate || undefined} />
                        {!isLeave && <input type="time" value={toTime} onChange={(e) => setToTime(e.target.value)} className="px-3 py-2 text-sm rounded-xl bg-white" style={{ border: '1px solid #d0c8be' }} />}
                    </div>
                </div>

                {type === 'annual_leave' && (
                    <div className="rounded-xl px-3 py-2 text-xs" style={{ border: '1px solid #e0d6cc', background: '#f6f3ee' }}>
                        <p className="text-gray-600">{t('ot.form.annual_hint')}</p>
                    </div>
                )}

                {type === 'overtime' && (
                    <Field label={t('ot.form.hours')}>
                        <input type="number" step="0.5" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="0" className="w-full px-3 py-2 text-sm rounded-xl" style={{ border: '1px solid #e0d6cc' }} />
                    </Field>
                )}

                {type === 'reimbursement' && (
                    <>
                        <Field label={t('ot.form.category')}>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {REIMBURSEMENT_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label={t('ot.form.amount')}>
                            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" className="w-full px-3 py-2 text-sm rounded-xl" style={{ border: '1px solid #e0d6cc' }} />
                        </Field>
                    </>
                )}

                {type === 'visit' && (
                    <Field label={t('ot.form.location')}>
                        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={t('ot.form.location_placeholder')} className="w-full px-3 py-2 text-sm rounded-xl" style={{ border: '1px solid #e0d6cc' }} />
                    </Field>
                )}

                <Field label={t('ot.form.reason')}>
                    <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder={t('ot.form.reason_placeholder')} className="w-full px-3 py-2 text-sm rounded-xl" style={{ border: '1px solid #e0d6cc' }} />
                </Field>

                {allowAttachment && (
                    <Field label={type === 'sick_leave' ? t('ot.form.sick_note') : t('ot.form.attachments')}>
                        <label className="flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer hover:bg-gray-50 transition" style={{ border: '1px dashed #d0c8be' }}>
                            <Briefcase className="h-4 w-4 text-gray-400" />
                            <span className="text-xs text-gray-600 flex-1">{t('ot.form.sick_note_hint')}</span>
                            <input type="file" accept="image/*,application/pdf" onChange={handleUpload} disabled={uploadingAttachment} className="hidden" />
                        </label>
                        {uploadingAttachment && <p className="text-[10px] text-gray-500 mt-1">{t('clock.uploading')}</p>}
                        {ocrBusy && <p className="text-[10px] text-gray-500 mt-1">AI OCR membaca struk...</p>}
                        {!ocrBusy && ocrSummary && <p className="text-[10px] text-green-700 mt-1">{ocrSummary}</p>}
                        {attachments.length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {attachments.map((a, i) => (
                                    <li key={i} className="flex items-center gap-2 text-xs">
                                        <a href={a.url} target="_blank" rel="noopener" className="text-blue-600 underline truncate flex-1">{a.filename}</a>
                                        <button type="button" onClick={() => removeAttachment(i)} className="text-red-500">×</button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Field>
                )}
            </div>
        </BottomSheet>
    );
}

function Field({ label, children }: { label: string; children: any }) {
    return (
        <div>
            <label className="text-xs font-bold text-gray-700 mb-1 block">{label}</label>
            {children}
        </div>
    );
}
